import React, { useState, useCallback, useEffect } from 'react';

const API_URL =
  import.meta.env.VITE_RAG_API_URL ||
  import.meta.env.VITE_API_URL ||
  'https://krow7dgex4.execute-api.eu-west-2.amazonaws.com';

interface CaseStudyUploadProps {
  user: { username?: string; email?: string };
  getToken: () => string | null;
  onClose: () => void;
  onSuccess?: (caseStudyText: string, title?: string, companyName?: string) => void;
}

interface CaseStudyStatus {
  has_case_study: boolean;
  vector_count?: number;
  message?: string;
}

const CaseStudyUpload: React.FC<CaseStudyUploadProps> = ({
  user,
  getToken,
  onClose,
  onSuccess,
}) => {
  const [caseStudyText, setCaseStudyText] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [companyName, setCompanyName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [csStatus, setCsStatus] = useState<CaseStudyStatus | null>(null);
  const [checkingStatus, setCheckingStatus] = useState<boolean>(true);

  const username = user?.username || user?.email?.split('@')[0] || 'unknown';

  const checkStatus = useCallback(async () => {
    try {
      setCheckingStatus(true);
      const response = await fetch(
        `${API_URL}/case-study/status?username=${encodeURIComponent(username)}`
      );
      if (response.ok) {
        const data = await response.json();
        setCsStatus(data);
      } else {
        setCsStatus({ has_case_study: false, message: 'Failed to check status' });
      }
    } catch (err) {
      console.error('Error checking case study status:', err);
      setCsStatus({ has_case_study: false, message: 'Failed to check status' });
    } finally {
      setCheckingStatus(false);
    }
  }, [username]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!caseStudyText || caseStudyText.trim().length < 50) {
      setError('Please enter the case study content (minimum 50 characters).');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      const token = getToken();
      if (!token) {
        setError('No authentication token available. Please log in again.');
        return;
      }

      const response = await fetch(`${API_URL}/case-study/upload`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${token}`,
        },
        body: JSON.stringify({
          username,
          case_study_text: caseStudyText.trim(),
          title: title.trim() || null,
          company_name: companyName.trim() || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to upload case study: ${response.statusText}`);
      }

      await response.json();
      setSuccess(true);
      await checkStatus();

      if (onSuccess) {
        setTimeout(() => {
          onSuccess(caseStudyText.trim(), title.trim() || undefined, companyName.trim() || undefined);
        }, 1000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload case study. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-3 sm:p-5 animate-fade-in"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl sm:rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center p-4 sm:p-6 pb-4 sm:pb-6 border-b-2 border-slate-200 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-t-xl sm:rounded-t-2xl">
          <h2 className="text-lg sm:text-2xl font-semibold m-0">
            📑 Case Study Prep
          </h2>
          <button
            className="bg-white/20 hover:bg-white/30 border-none text-white text-2xl sm:text-3xl w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center transition-all duration-200 leading-none p-0 hover:scale-110"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="p-4 sm:p-6 lg:p-8">
          {/* Status */}
          {checkingStatus ? (
            <div className="mb-4 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center gap-2 text-slate-600 text-sm">
                <div className="w-4 h-4 border-2 border-slate-300 border-t-amber-500 rounded-full animate-spin"></div>
                <span>Checking status...</span>
              </div>
            </div>
          ) : csStatus ? (
            <div className={`mb-4 p-3 rounded-lg border ${csStatus.has_case_study ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-2">
                {csStatus.has_case_study ? (
                  <>
                    <span className="text-green-600 text-lg">✓</span>
                    <div className="flex-1">
                      <p className="text-green-800 font-medium text-sm m-0">Case Study Ready</p>
                      {csStatus.vector_count !== undefined && (
                        <p className="text-green-600 text-xs m-0 mt-1">{csStatus.vector_count} sections indexed</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <span className="text-slate-400 text-lg">○</span>
                    <p className="text-slate-600 text-sm m-0">{csStatus.message || 'No case study uploaded yet'}</p>
                  </>
                )}
              </div>
            </div>
          ) : null}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 font-medium text-sm m-0">
                ✓ Case study {csStatus?.has_case_study ? 'updated' : 'uploaded'} successfully! Ready to start.
              </p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm m-0 whitespace-pre-line">{error}</p>
            </div>
          )}

          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h3 className="text-amber-800 font-semibold text-sm mb-2 mt-0">How Case Study Prep Works</h3>
            <ul className="text-amber-700 text-xs space-y-1 m-0 pl-4">
              <li>Paste the full case study brief, prompt, or scenario</li>
              <li>The AI will analyze the data, constraints, and requirements</li>
              <li>You'll be guided through a structured case interview</li>
              <li>Practice structuring, analyzing, and presenting recommendations</li>
              <li>Great for consulting interviews, strategy roles, and PM case rounds</li>
            </ul>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label htmlFor="cs-title" className="block mb-2 text-slate-700 font-semibold text-sm">
                  Case Study Title (Optional)
                </label>
                <input
                  id="cs-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="e.g., Market Entry Strategy, Revenue Optimization..."
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm transition-colors focus:outline-none focus:border-amber-500 bg-white"
                  disabled={loading}
                />
              </div>
              <div>
                <label htmlFor="cs-company" className="block mb-2 text-slate-700 font-semibold text-sm">
                  Company Name (Optional)
                </label>
                <input
                  id="cs-company"
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., McKinsey, BCG, Google..."
                  className="w-full px-3 py-2.5 border-2 border-slate-200 rounded-lg text-sm transition-colors focus:outline-none focus:border-amber-500 bg-white"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="case-study-text" className="block mb-2 text-slate-700 font-semibold text-sm sm:text-base">
                Case Study Content *
              </label>
              <textarea
                id="case-study-text"
                value={caseStudyText}
                onChange={(e) => setCaseStudyText(e.target.value)}
                placeholder={`Paste the full case study here including:
- Problem statement or scenario
- Background context and industry
- Data, metrics, and KPIs provided
- Constraints (budget, timeline, resources)
- Any specific questions or deliverables expected
- Exhibits, charts, or data tables (describe them in text)

Example: "Your client is a mid-size retailer with $500M in annual revenue..."`}
                className="w-full min-h-[300px] px-3 py-2.5 sm:py-3 border-2 border-slate-200 rounded-lg text-sm sm:text-base transition-colors focus:outline-none focus:border-amber-500 bg-white resize-y"
                disabled={loading}
              />
              <p className="mt-2 text-xs text-slate-500">
                The more detail you provide, the more realistic and targeted the case interview will be.
              </p>
              {caseStudyText && (
                <div className={`mt-2 text-xs ${caseStudyText.trim().length >= 50 ? 'text-green-600' : 'text-amber-600'}`}>
                  {caseStudyText.trim().length} characters entered{' '}
                  {caseStudyText.trim().length < 50 && '(minimum 50 required)'}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-6">
              <button
                type="submit"
                disabled={loading || !caseStudyText || caseStudyText.trim().length < 50}
                className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:from-amber-600 hover:to-orange-700 hover:-translate-y-0.5 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    Processing...
                  </span>
                ) : csStatus?.has_case_study ? (
                  'Update & Start Case Study'
                ) : (
                  'Start Case Study Prep'
                )}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-3 sm:py-4 text-base sm:text-lg bg-slate-200 text-slate-700 rounded-lg border-none cursor-pointer font-semibold transition-all duration-200 hover:bg-slate-300"
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>

          {csStatus?.has_case_study && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-blue-800 text-xs sm:text-sm m-0">
                <strong>Note:</strong> Uploading new content will replace your existing case study.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CaseStudyUpload;
